import McmVideoConfig, {
  McmVideoAgent,
  McmVideoDuration,
  McmVideoOrientation,
  McmVideoType,
} from '~/tenants/mcm/performable/video/McmVideoConfig';
import McmJob from '~/tenants/mcm/model/McmJob';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { TenantJobLine } from '~/lib/model';
import { Big } from 'big.js';
import { DeliverableType } from '~common/model/Deliverable';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { McmOrderContext } from '~/tenants/mcm/model/McmOrder';
import { context_has_buyer } from '~lib/context';
import { match } from 'ts-pattern';
import { MCM_VIDEO_HIGHLIGHT_TIERS, MCM_VIDEO_PREMIUM_TIERS, MCM_VIDEO_LUXURY_TIERS } from '~/tenants/mcm/data';

export default class McmVideoJob extends McmJob<typeof McmVideoConfig> {
  get performable() {
    return McmVideoConfig;
  }

  defaultValue(): ZodVersionedMetadata<(typeof McmVideoConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      type: McmVideoType.HIGHLIGHT,
      orientation: McmVideoOrientation.HORIZONTAL,
    };
  }

  get deliverable() {
    return DeliverableType.VIDEO;
  }

  get media() {
    return PersonaType.PROVIDER;
  }

  get name(): string {
    return this.calculate.name;
  }

  revenueLines(): TenantJobLine[] {
    const lines: TenantJobLine[] = [];

    // if (this.orderHasPerformable('photo') && this.buyer('crossroads')) {
    //   return lines;
    // }

    const { name, revenue } = this.calculate;

    lines.push({
      id: 'video',
      description: name,
      amount: revenue,
      taxable: true,
    });

    if (this.metadata.social_edit) {
      lines.push({
        id: 'social_edit',
        description: 'Social Edit (60 seconds)',
        compare: 'Social Edit',
        amount: new Big(100),
        taxable: true,
      });
    }

    if (this.metadata.extra) {
      for (const extra of this.metadata.extra) {
        lines.push({
          id: `extra-${extra.orientation}-${extra.duration}`,
          description: `${extra.orientation} ${extra.duration}`,
          amount: McmVideoJob.duration(extra.duration),
          taxable: true,
        });
      }
    }

    if (this.metadata.agent) {
      const calc = McmVideoJob.agent(this.order, this.metadata.agent);

      lines.push({
        id: 'agent',
        description: calc.name,
        amount: calc.amount,
        taxable: true,
      });
    }

    if (this.metadata.day_to_night) {
      lines.push({
        id: 'day_to_night',
        description: 'Day to Night Transitions',
        compare: true,
        amount: new Big(350),
        taxable: true,
      });
    }

    return lines;
  }

  onsite(): number {
    return this.calculate.onsite;
  }

  static duration(duration: McmVideoDuration) {
    return match(duration)
      .with(McmVideoDuration.AUDIO30, () => new Big(65))
      .with(McmVideoDuration.AUDIO60, () => new Big(100))
      .with(McmVideoDuration.AUDIO90, () => new Big(125))
      .with(McmVideoDuration.AUDIO180, () => new Big(150))
      .otherwise(() => new Big(35));
  }

  static agent(context: McmOrderContext, type: McmVideoAgent) {
    if (type === McmVideoAgent.STORY) {
      return {
        name: 'Storytelling',
        amount: new Big(250),
      };
    }

    return {
      name: 'Intro / Midroll / Outro',
      amount: new Big(context_has_buyer(context, 'crossroads') ? 50 : 100),
    };
  }

  static calculate(context: McmOrderContext, type: McmVideoType) {
    const sqft = context.metadata.sqft || 2000;
    let amount = new Big(0);
    let increment = 0;
    let onsite = 0;
    let duration = 'Up to 60 Seconds';
    let name = '';

    switch (type) {
      case McmVideoType.HIGHLIGHT:
        name = 'Highlight Reel';

        // Use tier-based pricing for highlight reel
        const tier = MCM_VIDEO_HIGHLIGHT_TIERS.find(t => sqft <= t.max);
        if (tier) {
          amount = tier.price;
          onsite = tier.onsite;
          increment = 0; // No additional calculation needed
        } else {
          // Beyond max tier, use formula
          const baseTier = MCM_VIDEO_HIGHLIGHT_TIERS[MCM_VIDEO_HIGHLIGHT_TIERS.length - 1];
          amount = baseTier.price;
          onsite = baseTier.onsite;
          increment = 25; // Will be applied below

          // Calculate additional sqft beyond the highest tier
          const additionalSqft = Math.max(0, sqft - baseTier.max);
          amount = amount.plus(new Big(Math.ceil(additionalSqft / 500)).times(increment));
          onsite = onsite + Math.ceil(additionalSqft / 500) * 5;
        }
        break;
      case McmVideoType.PREMIUM:
        name = 'Premium Video';
        duration = '1-2 Minutes';

        // Use tier-based pricing for premium video
        const premiumTier = MCM_VIDEO_PREMIUM_TIERS.find(t => sqft <= t.max);
        if (premiumTier) {
          amount = premiumTier.price;
          onsite = premiumTier.onsite;
          increment = 0; // No additional calculation needed
        } else {
          // Beyond max tier, use formula
          const baseTier = MCM_VIDEO_PREMIUM_TIERS[MCM_VIDEO_PREMIUM_TIERS.length - 1];
          amount = baseTier.price;
          onsite = baseTier.onsite;
          increment = 25; // Will be applied below

          // Calculate additional sqft beyond the highest tier
          const additionalSqft = Math.max(0, sqft - baseTier.max);
          amount = amount.plus(new Big(Math.ceil(additionalSqft / 500)).times(increment));
          onsite = onsite + Math.ceil(additionalSqft / 500) * 5;
        }
        break;
      case McmVideoType.LUXURY:
        name = 'Luxury Video';
        duration = '2-3 Minutes';

        // Use tier-based pricing for luxury video
        const luxuryTier = MCM_VIDEO_LUXURY_TIERS.find(t => sqft <= t.max);
        if (luxuryTier) {
          amount = luxuryTier.price;
          onsite = luxuryTier.onsite;
          increment = 0; // No additional calculation needed
        } else {
          // Beyond max tier, use formula
          const baseTier = MCM_VIDEO_LUXURY_TIERS[MCM_VIDEO_LUXURY_TIERS.length - 1];
          amount = baseTier.price;
          onsite = baseTier.onsite;
          increment = 50; // Will be applied below

          // Calculate additional sqft beyond the highest tier
          const additionalSqft = Math.max(0, sqft - baseTier.max);
          amount = amount.plus(new Big(Math.ceil(additionalSqft / 500)).times(increment));
          onsite = onsite + Math.ceil(additionalSqft / 500) * 10; // 10 minutes per 500 sqft for luxury
        }
        break;
    }

    // All video types now use tier-based pricing
    const result = {
      name,
      revenue: amount,
      duration,
      onsite: onsite,
    };

    if (context_has_buyer(context, 'crossroads')) {
      result.revenue = new Big(350);
    }

    return result;
  }

  protected get calculate() {
    return McmVideoJob.calculate(this.order, this.metadata.type);
  }

  get configurable() {
    return true;
  }

  get hidden() {
    return Boolean(this.buyer('crossroads')) && !this.orderHasPerformable('zillow');
  }
}
